import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ArticleSeo } from "../components/buying-advice/ArticleSeo";
import {
  ArticleCardHero,
  ArticleCard,
  ArticleCardCompact,
  CategoryChip,
} from "../components/buying-advice/ArticleCards";
import {
  fetchArticles,
  fetchArticleCategories,
  searchArticles,
} from "../lib/articlesApi";

/**
 * Buying Advice landing page.
 *
 * Layout (top to bottom):
 *   • Hero (title + intro + search box)
 *   • Featured article (large card)
 *   • Latest articles (grid)
 *   • Popular articles (compact list)
 *   • Categories (chips linking to filtered view)
 */
export default function BuyingAdvice() {
  const [featured, setFeatured] = useState(null);
  const [latest, setLatest] = useState([]);
  const [popular, setPopular] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [featResp, cats] = await Promise.all([
          fetchArticles({ featured: true, limit: 1 }),
          fetchArticleCategories(),
        ]);
        if (!alive) return;
        const featItem = (featResp.items && featResp.items[0]) || null;
        setFeatured(featItem);
        setCategories(cats.items || []);
        const latestResp = await fetchArticles({
          limit: 6,
          exclude: featItem?.slug || undefined,
        });
        if (!alive) return;
        setLatest(latestResp.items || []);
        const popResp = await fetchArticles({ popular: true, limit: 4 });
        if (!alive) return;
        setPopular(popResp.items || []);
      } catch (e) {
        console.warn("buying-advice load failed", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Debounced live search
  useEffect(() => {
    if (!query || query.length < 2) {
      setSearchResults(null);
      return undefined;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await searchArticles(query, 20);
        setSearchResults(r.items || []);
      } catch (e) {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const displayedLatest = useMemo(() => {
    if (searchResults) return searchResults;
    if (activeCategory) {
      return latest.filter((a) => a.category_slug === activeCategory);
    }
    return latest;
  }, [latest, activeCategory, searchResults]);

  const canonical =
    typeof window !== "undefined"
      ? `${window.location.origin}/buying-advice`
      : "https://shouldibuyit.co.uk/buying-advice";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ArticleSeo
        title="Buying Advice — Guides, Comparisons & Smart Shopping Tips | Should I Buy It"
        description="Independent buying guides, product comparisons, and Amazon-shopping tips from the Should I Buy It team. No sponsored fluff — just clear advice that helps you buy well."
        canonicalUrl={canonical}
        type="website"
      />
      <Header />

      <main className="max-w-6xl mx-auto px-5 pb-24">
        {/* HERO */}
        <section className="pt-14 sm:pt-20 pb-12 sm:pb-16 border-b border-border">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
            The Should I Buy It Knowledge Centre
          </div>
          <h1
            data-testid="buying-advice-hero-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-5 max-w-3xl"
          >
            Buying Advice
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mb-8">
            Independent guides, real product comparisons, and the shopping
            tips we use ourselves. No sponsored fluff — just clear, honest
            advice that helps you buy well and stop second-guessing.
          </p>
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              data-testid="buying-advice-search-input"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles — try 'air fryer' or 'best time to buy'"
              className="w-full h-12 pl-11 pr-10 rounded-xl bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 text-sm placeholder:text-muted-foreground"
            />
            {query ? (
              <button
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md hover:bg-secondary flex items-center justify-center transition"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>
        </section>

        {/* FEATURED */}
        {featured && !searchResults ? (
          <section className="py-14 sm:py-20 border-b border-border">
            <ArticleCardHero article={featured} />
          </section>
        ) : null}

        {/* SEARCH RESULTS OR LATEST + POPULAR */}
        {searchResults ? (
          <section className="py-12" data-testid="buying-advice-search-results">
            <div className="flex items-baseline justify-between mb-8">
              <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {searching
                  ? "Searching..."
                  : `Results for “${query}” — ${searchResults.length}`}
              </h2>
              <button
                onClick={() => setQuery("")}
                className="text-xs text-muted-foreground hover:text-foreground transition"
              >
                Clear
              </button>
            </div>
            {searchResults.length === 0 && !searching ? (
              <p className="text-muted-foreground text-sm">
                No articles matched. Try broader terms like "TV" or "air fryer".
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {searchResults.map((a) => (
                  <ArticleCard key={a.slug} article={a} />
                ))}
              </div>
            )}
          </section>
        ) : (
          <div className="grid lg:grid-cols-[1fr_320px] gap-14 py-14 sm:py-20 border-b border-border">
            {/* LATEST */}
            <section>
              <div className="flex items-baseline justify-between mb-8">
                <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  Latest
                </h2>
                {activeCategory ? (
                  <button
                    onClick={() => setActiveCategory(null)}
                    className="text-xs text-muted-foreground hover:text-foreground transition"
                  >
                    Clear filter
                  </button>
                ) : null}
              </div>
              <div
                data-testid="buying-advice-latest-list"
                className="grid sm:grid-cols-2 gap-x-8 gap-y-12"
              >
                {displayedLatest.map((a) => (
                  <ArticleCard key={a.slug} article={a} />
                ))}
                {displayedLatest.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    No articles in this category yet.
                  </p>
                ) : null}
              </div>
            </section>

            {/* POPULAR (sidebar on desktop) */}
            <aside>
              <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
                Popular
              </h2>
              <div className="space-y-5" data-testid="buying-advice-popular-list">
                {popular.map((a) => (
                  <ArticleCardCompact key={a.slug} article={a} />
                ))}
                {popular.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    Popular articles will appear here as readers engage.
                  </p>
                ) : null}
              </div>
            </aside>
          </div>
        )}

        {/* CATEGORIES */}
        {!searchResults && categories.length > 0 ? (
          <section className="py-14 sm:py-20" data-testid="buying-advice-categories">
            <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              Browse by category
            </h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveCategory(null)}
                className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition border ${
                  !activeCategory
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-secondary"
                }`}
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c.slug}
                  onClick={() => setActiveCategory(c.slug)}
                  data-testid={`buying-advice-cat-${c.slug}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition border ${
                    activeCategory === c.slug
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:bg-secondary"
                  }`}
                >
                  {c.name}
                  <span className="text-[10px] opacity-70">({c.count})</span>
                </button>
              ))}
            </div>
          </section>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
